import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /**
   * Re-rendering the cart section can leave the drawer open but out of the top
   * layer: morphing re-adds `open` to a dialog that closed meanwhile, or moving
   * an ancestor drops its modal state. It then sits pinned inside the header
   * with no backdrop, so put it back as a modal, keeping the page's scroll lock.
   */
  #modalGuard = new MutationObserver(() => this.#restoreModal());

  #restoreModal() {
    const { dialog } = this.refs;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open || dialog.matches(':modal')) return;

    const lockedTop = document.body.style.position === 'fixed' ? document.body.style.top : null;
    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.showDialog();
    if (lockedTop !== null) document.body.style.top = lockedTop;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#modalGuard.observe(this, { attributes: true, attributeFilter: ['open'], childList: true, subtree: true });
    // Moved by a re-render (disconnected, then connected again): check it straight away.
    this.#restoreModal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#modalGuard.disconnect();
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
